import { useState } from "react";
import { useNavigate } from "react-router";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { analytics, ANALYTICS_EVENTS } from "@/lib/analytics";
import { toDiscover } from "@/lib/urls";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/use-media-query";

interface NavSearchProps {
  className?: string;
  /** On its own full-width row, where the long placeholder always fits. */
  fullWidth?: boolean;
}

/**
 * Site-wide search in the header. Submitting hands the query to Discover,
 * which searches projects, people, portfolios and funding pools at once.
 */
export const NavSearch = ({ className, fullWidth = false }: NavSearchProps) => {
  const navigate = useNavigate();
  // Inline below ~900px the header is crowded, so keep the placeholder short.
  const isCramped = useMediaQuery("(max-width: 900px)") && !fullWidth;
  const [value, setValue] = useState("");

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = value.trim();
    if (q) {
      analytics.track(ANALYTICS_EVENTS.DISCOVER_QUERY_SUBMITTED, {
        length: q.length,
        source: "navbar",
      });
    }
    navigate(toDiscover({ q }));
    setValue("");
  };

  return (
    <form
      role="search"
      onSubmit={handleSubmit}
      className={cn("w-full", className)}
    >
      {/* The icon centres on this wrapper, not the form, so padding or
          margins a caller puts on the form can't knock it off-centre. */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={
            isCramped ? "Search Catalyze" : "Search projects, people, funding"
          }
          aria-label="Search Catalyze"
          className="h-9 rounded-full bg-muted/50 pl-9"
        />
      </div>
    </form>
  );
};
